import React from 'react';
import {
  Box,
  Button,
  Card,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { pagePaths } from 'routes/paths';
import { Achievement } from '../mockStats';

interface AchievementsCardProps {
  next: Achievement;
  recent: Achievement[];
}

const AchievementsCard: React.FC<AchievementsCardProps> = ({ next, recent }) => {
  const pct = Math.min(100, Math.round((next.current / next.threshold) * 100));
  const remaining = Math.max(0, next.threshold - next.current);

  return (
    <Card sx={{ height: '100%' }}>
      <Stack spacing={2.5} sx={{ height: '100%' }}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            <EmojiEventsOutlinedIcon sx={{ color: 'primary.main' }} />
            <Typography variant={'h3'}>{'Нагороди'}</Typography>
          </Stack>
          <Button
            component={Link}
            to={'/' + pagePaths.achievements}
            endIcon={<ArrowForwardIcon />}
            variant={'text'}
            size={'small'}
            sx={{ px: 1 }}
          >
            {'Усі'}
          </Button>
        </Stack>

        <Box>
          <Typography color={'text.secondary'} variant={'body2'}>
            {'Найближча нагорода'}
          </Typography>
          <Typography variant={'h5'} sx={{ mt: 0.5 }}>
            {next.title}
          </Typography>
          <Typography color={'text.secondary'} variant={'body2'} sx={{ mt: 0.25 }}>
            {next.description}
          </Typography>
          <LinearProgress
            value={pct}
            variant={'determinate'}
            sx={{
              bgcolor: 'primary.purple',
              borderRadius: 10,
              height: 10,
              mt: 1.5,
              '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
            }}
          />
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            sx={{ mt: 0.75 }}
          >
            <Typography variant={'caption'} color={'text.secondary'}>
              {`${next.current} / ${next.threshold}`}
            </Typography>
            <Typography
              variant={'caption'}
              sx={{ color: 'primary.main', fontWeight: 700 }}
            >
              {remaining ? `Ще ${remaining}` : 'Готова до відкриття'}
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Typography color={'text.secondary'} variant={'body2'} sx={{ mb: 1 }}>
            {'Отримані'}
          </Typography>
          <Stack direction={'row'} spacing={1.25} flexWrap={'wrap'}>
            {recent.map((a) => (
              <Tooltip
                key={a.code}
                arrow
                placement={'top'}
                title={
                  <Box>
                    <Typography variant={'caption'} fontWeight={700}>
                      {a.title}
                    </Typography>
                    <br />
                    <Typography variant={'caption'}>{a.description}</Typography>
                    {a.unlockedAt && (
                      <>
                        <br />
                        <Typography variant={'caption'} color={'text.secondary'}>
                          {a.unlockedAt}
                        </Typography>
                      </>
                    )}
                  </Box>
                }
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    bgcolor: 'primary.purple',
                    borderRadius: '50%',
                    color: 'primary.main',
                    cursor: 'pointer',
                    display: 'flex',
                    height: 44,
                    justifyContent: 'center',
                    transition: 'transform 80ms ease',
                    width: 44,
                    '&:hover': { transform: 'scale(1.08)' },
                  }}
                >
                  <MilitaryTechIcon />
                </Box>
              </Tooltip>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};

export default AchievementsCard;
